/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  ComponentContextCreateOptions,
  SingleValueParameter,
  ReportDefinition,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService, dictionaryService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { MDDEResult, SortingOptions } from "@sa/rep-mdde";
import { SelectionMode, AxisPosition, AggregationType, SortingDirection } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, MDDEQuery, OLAPTransformer } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { uniq } from "../../../../shared";

export class ContextReport1 implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public payloadChart1 = new BehaviorSubject<OLAPResult | null>(null);
  public payloadChart2 = new BehaviorSubject<OLAPResult | null>(null);
  public payloadChart3 = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);

  protected _calendar: BehaviorSubject<string[]>;
  public get calendar(): Observable<string[]> {
    return this._calendar.asObservable();
  }

  protected _cropCalendar: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public get cropCalendar(): Observable<string> {
    return this._cropCalendar.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as MultiValueParameter;
    this._calendar = new BehaviorSubject(calendarParameter.value as string[]);
    const cropCalendarParameter = createOptions.dataflowService.getParameter("@crop_calendar") as SingleValueParameter;
    this._cropCalendar = new BehaviorSubject(cropCalendarParameter.value as string);

    combineLatest([this._calendar, this._cropCalendar])
      .pipe(debounceTime(500))
      .subscribe(([calendar, cropCalendar]) => {
        this.query({ calendar, cropCalendar });
      });
  }

  async tryIntegrate() {
    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {
      this.readyState = ComponentContextReadyState.Loading;
      this.dataflowIntegration.integrated = true;
      const cube = await dataService.getCubeDefinition("harm");
      this.cube.next(cube);
      this.readyState = ComponentContextReadyState.Loaded;
    }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar");
    if (calendar !== undefined) {

      this._calendar.next(calendar as string[]);
    }
    const cropCalendar = command.values.get("@crop_calendar");
    if (cropCalendar !== undefined) {
      this._cropCalendar.next(cropCalendar as string);
    }
  }

  async query(parameters: { calendar: string[]; cropCalendar: string }) {
    const { calendar, cropCalendar } = parameters;
    const dictionaryCalendarGI = await dictionaryService.getDictionary({
      id: "calendar-gi",
      parameters: {},
    });
    const itemsDictionaryCalendarGI = await dictionaryCalendarGI.getAll();

    // if (this.dataflowIntegration.canIntegrate && this.dataflowIntegration.integrated) {
    this.readyState = ComponentContextReadyState.Loading;
    this.payloadChart1.next(null);
    this.payloadChart2.next(null);
    const def = await dataService.getCubeDefinition("stocktouseratio");
    const cube = await dataService.getCube({
      id: "stocktouseratio",
      parameters: def.parameters,
    });

    const defGI = await dataService.getCubeDefinition("gi");
    defGI.parameters["StorageParameter2"]!["defaultValue"] = Number(cropCalendar);
    const cubeGI = await dataService.getCube({
      id: "gi",
      parameters: defGI.parameters,
    });

    const olapResult1$ = cube.query({
      columns: {
        showEmpty: false,
        parts: [{ path: "calendar.default", selection: calendar?.map(Number), selectionMode: SelectionMode.Include }],
      },
      rows: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: [100231],
            selectionMode: SelectionMode.Include,
          },
          {
            path: "Measures",
            measures: [{ id: "value", aggregationType: AggregationType.Sum },
            { id: "search", aggregationType: AggregationType.None }],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: "stocktouseratio",
      },
    } as any);
    // TODO: Получение параметров по умолчанию из this.model

    const giCalendarIds = itemsDictionaryCalendarGI
      .filter((x) => calendar?.includes(x.values["year"] as any))
      .map((x) => Number(x.id));

    const olapResult2$ = cubeGI.query({
      columns: {
        showEmpty: false,
        parts: [{ path: "calendar.default", selection: giCalendarIds, selectionMode: SelectionMode.Include }],
      },
      filter: {
        showEmpty: false,
        parts: [
          {
            path: "commodities.default",
            selection: [3001, 3002, 3003, 3004],
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          {
            path: "Measures",
            measures: [{ id: "value", aggregationType: AggregationType.Sum },
            { id: "search", aggregationType: AggregationType.None }],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: "gi",
      },
    } as any);

    // const olapResult3 = await cube.query({
    //   columns: {
    //     showEmpty: false,
    //     parts: [
    //       {
    //         path: "countries.default",
    //         selection: [4007, 3030, 4003, 1009, 3007, 1014, 3002, 3006],
    //         selectionMode: SelectionMode.Include,
    //       },
    //     ],
    //   },
    //   filters: {
    //     showEmpty: false,
    //     parts: [
    //       { path: "attributes.default", selection: [30], selectionMode: SelectionMode.Include },
    //       { path: "commodities.default", selection: [1101], selectionMode: SelectionMode.Include },
    //     ],
    //   },
    //   rows: {
    //     showEmpty: false,
    //     parts: [
    //       {
    //         path: "calendar.default",
    //         selection: calendar.map((x) => Number(x)),
    //         selectionMode: SelectionMode.Include,
    //       },
    //       { path: "Measures", measures: [{ id: "value", aggregationType: AggregationType.Sum }] },
    //     ],
    //   },
    //   sourceResolveOptions: {
    //     sourceId: "harm",
    //   },
    // } as any);
    // this.payloadChart3.next(olapResult3);

    this.readyState = ComponentContextReadyState.Loaded;
    let [olapResult1, olapResult2] = await Promise.all([olapResult1$, olapResult2$]);

    const { dataSourcesService } = this.options;
    const olapResult1Sources$ = dataSourcesService.getDataSource(olapResult1);
    const olapResult2Sources$ = dataSourcesService.getDataSource(olapResult2);
    const [olapResult1Sources, olapResult2Sources] = await Promise.all([olapResult1Sources$, olapResult2Sources$]);
    this.sources$.next(uniq([
      olapResult1Sources, olapResult2Sources
    ]).filter(Boolean).join(', '));
    olapResult1 = dataSourcesService.clearDataSources(olapResult1);
    olapResult2 = dataSourcesService.clearDataSources(olapResult2);

    this.payloadChart1.next(olapResult1);
    this.payloadChart2.next(olapResult2);
    // }
  }
}
